import { api } from "features/api";
import type {
  LogoUploadResponse,
  Partner,
  PartnerAdmin,
  PartnerCreateRequest,
  PartnerDetail,
  PartnerFilters,
  PartnershipInquiryRequest,
  PartnershipInquiryResponse,
  PartnerUpdateRequest,
  RelatedPartner,
} from "types/partners";

export const partnersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Public endpoints
    getPartners: builder.query<
      { results: Partner[]; count: number },
      PartnerFilters
    >({
      query: (filters) => ({
        url: "partners/",
        params: {
          search: filters.search || undefined,
          partner_type: filters.partner_type || undefined,
          industry_type: filters.industry_type || undefined,
          featured: filters.featured || undefined,
          ordering: filters.sort || "name",
          page: filters.page || undefined,
          page_size: filters.page_size || undefined,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({
                type: "Partners" as const,
                id,
              })),
              { type: "Partners", id: "LIST" },
            ]
          : [{ type: "Partners", id: "LIST" }],
    }),

    getPartner: builder.query<PartnerDetail, string>({
      query: (slug) => `partners/${slug}/`,
      providesTags: (result, error, slug) => [{ type: "Partners", id: slug }],
    }),

    getRelatedPartners: builder.query<
      { results: RelatedPartner[]; count: number },
      string
    >({
      query: (slug) => `partners/${slug}/related/`,
    }),

    submitPartnershipInquiry: builder.mutation<
      PartnershipInquiryResponse,
      PartnershipInquiryRequest
    >({
      query: (data) => ({
        url: "partners/inquiry/",
        method: "POST",
        body: data,
      }),
    }),

    // Admin endpoints
    getPartnersAdmin: builder.query<
      { results: PartnerAdmin[]; count: number },
      {
        search?: string;
        active_only?: boolean;
        ordering?: string;
        page?: number;
        page_size?: number;
      }
    >({
      query: (params) => ({
        url: "partners/admin/",
        params,
      }),
      providesTags: [{ type: "Partners", id: "ADMIN_LIST" }],
    }),

    createPartner: builder.mutation<PartnerAdmin, PartnerCreateRequest>({
      query: (data) => ({
        url: "partners/admin/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        { type: "Partners", id: "LIST" },
        { type: "Partners", id: "ADMIN_LIST" },
      ],
    }),

    updatePartner: builder.mutation<
      PartnerAdmin,
      { slug: string; data: PartnerUpdateRequest }
    >({
      query: ({ slug, data }) => ({
        url: `partners/admin/${slug}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { slug }) => [
        { type: "Partners", id: slug },
        { type: "Partners", id: "LIST" },
        { type: "Partners", id: "ADMIN_LIST" },
      ],
    }),

    deletePartner: builder.mutation<void, string>({
      query: (slug) => ({
        url: `partners/admin/${slug}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Partners", id: "LIST" },
        { type: "Partners", id: "ADMIN_LIST" },
      ],
    }),

    togglePartnerFeatured: builder.mutation<
      { id: number; slug: string; is_featured: boolean },
      string
    >({
      query: (slug) => ({
        url: `partners/admin/${slug}/featured/`,
        method: "POST",
      }),
      invalidatesTags: (result, error, slug) => [
        { type: "Partners", id: slug },
        { type: "Partners", id: "LIST" },
        { type: "Partners", id: "ADMIN_LIST" },
      ],
    }),

    uploadPartnerLogo: builder.mutation<LogoUploadResponse, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "partners/admin/upload-logo/",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [
        { type: "Partners", id: "LIST" },
        { type: "Partners", id: "ADMIN_LIST" },
      ],
    }),
  }),
});

export const {
  useGetPartnersQuery,
  useGetPartnerQuery,
  useGetRelatedPartnersQuery,
  useSubmitPartnershipInquiryMutation,
  useGetPartnersAdminQuery,
  useCreatePartnerMutation,
  useUpdatePartnerMutation,
  useDeletePartnerMutation,
  useTogglePartnerFeaturedMutation,
  useUploadPartnerLogoMutation,
} = partnersApi;
